import { CatalogueIcon, DispatchIcon, GrievancesIcon, KYCVerificationIcon } from 'core/components/Icons'
import { Badge } from 'core/components/ui/Badge'
import { Modal } from 'core/components/ui/Modal'
import { PageHeader } from 'core/components/ui/PageHeader'
import { Column, Table } from 'core/components/ui/Table'
import { orderRepository } from 'features/orders/data/repositories/OrderRepositoryImpl'
import { OrderDetail } from 'features/orders/domain/entities/Order'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { grievanceRepository } from '../../data/repositories/GrievanceRepositoryImpl'
import { AuditLedger, GrievanceRecord } from '../../domain/entities/Grievance'

interface FarmerSaleItem {
  id: string
  farmerName: string
  farmerPhone: string
  units: number
  pricePerUnit: number
  date: string
  isChild?: boolean
}

const grievanceStatusTones: Record<string, 'success' | 'warning' | 'danger'> = {
  resolved: 'success',
  in_progress: 'warning',
  open: 'danger',
}

const getGrievanceStatusTone = (status: string): 'success' | 'warning' | 'danger' => {
  return grievanceStatusTones[status] || 'danger'
}

const transformLedgerToFarmerSales = (ledgerData: AuditLedger | null): FarmerSaleItem[] => {
  if (!ledgerData) return []
  const realFarmers: FarmerSaleItem[] = []
  let count = 1

  // Direct FPO -> Farmer sales
  for (const f of ledgerData.fpoToFarmers || []) {
    realFarmers.push({
      id: `REAL-FS-${count}`,
      farmerName: f.farmerName,
      farmerPhone: f.phoneNumber || 'N/A',
      units: f.units,
      pricePerUnit: f.pricePerUnit || 0,
      date: 'Verified',
    })
    count += 1
  }

  // Downstream VLCC -> Farmer sales
  for (const v of ledgerData.fpoToVlcc || []) {
    realFarmers.push({
      id: `VLCC-${count}`,
      farmerName: `${v.vlccName} (VLCC)`,
      farmerPhone: 'N/A',
      units: v.unitsReceived,
      pricePerUnit: 0,
      date: 'VLCC Received',
    })
    count += 1

    for (const f of v.vlccToFarmers || []) {
      realFarmers.push({
        id: `VLCC-FS-${count}`,
        farmerName: `${f.farmerName} (Farmer)`,
        farmerPhone: f.phoneNumber || 'N/A',
        units: f.units,
        pricePerUnit: f.pricePerUnit || 0,
        date: 'VLCC Sale',
        isChild: true,
      })
      count += 1
    }
  }

  return realFarmers
}

export function GrievanceLedgerPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [grievance, setGrievance] = useState<GrievanceRecord | null>(null)
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  // Farmers distribution state
  const [farmersList, setFarmersList] = useState<FarmerSaleItem[]>([])
  const [page, setPage] = useState(1)
  const itemsPerPage = 8

  useEffect(() => {
    const loadDetails = async () => {
      if (!id) return
      setLoading(true)
      try {
        const match = await grievanceRepository.getGrievanceById(id)
        if (match) {
          setGrievance(match)
          const ord = await orderRepository.getOrderById(match.orderId)
          if (ord) {
            setOrder(ord)
          }

          // Fetch real audit ledger from backend API
          const ledgerData = await grievanceRepository.getAuditLedger(id)
          const realFarmers = transformLedgerToFarmerSales(ledgerData)
          setFarmersList(realFarmers)
        }
      } catch (err) {
        // NOSONAR: Logging ledger loading failure for diagnostics
        // eslint-disable-next-line no-console
        const errorVal = err as Error
        setError(errorVal.message || 'Failed to load grievance ledger.')
      } finally {
        setLoading(false)
      }
    }

    loadDetails()
  }, [id])

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-20 rounded-card bg-surface border border-border" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 rounded-card bg-surface border border-border" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-72 rounded-card bg-surface border border-border" />
          <div className="h-72 rounded-card bg-surface border border-border" />
        </div>
        <div className="h-96 rounded-card bg-surface border border-border" />
      </div>
    )
  }

  if (!grievance) {
    return (
      <div className="space-y-6">
        {error && (
          <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
            {error}
          </div>
        )}
        <div className="text-center py-12 bg-surface rounded-card border border-border p-4">
          <h3 className="text-lg font-bold text-text-primary">Grievance record not found</h3>
          <p className="text-xs text-text-secondary mt-1">The requested audit ledger ID could not be retrieved.</p>
          <button
            onClick={() => navigate('/grievances')}
            className="mt-4 px-4 py-2 bg-primary text-primary-fg font-semibold rounded-btn shadow"
          >
            Back to Queue
          </button>
        </div>
      </div>
    )
  }

  // Paginate farmers list
  const totalItems = farmersList.length
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const paginatedFarmers = farmersList.slice((page - 1) * itemsPerPage, page * itemsPerPage)

  const farmerColumns: Column<FarmerSaleItem>[] = [
    {
      key: 'farmerName',
      label: 'Farmer Name',
      className: 'font-semibold text-text-primary whitespace-nowrap',
      render: (row) => (
        <div className={`flex items-center ${row.isChild ? 'pl-4' : ''}`}>
          {row.isChild && <span className="text-text-secondary/50 mr-2 font-mono font-normal">└─</span>}
          <span className={row.isChild ? 'text-text-secondary font-medium' : ''}>{row.farmerName}</span>
        </div>
      ),
    },
    {
      key: 'farmerPhone',
      label: 'Phone Number',
      className: 'text-text-secondary font-medium font-mono whitespace-nowrap',
      render: (row) =>
        row.farmerPhone === 'N/A' ? <span className="text-text-secondary/50">-</span> : <span>{row.farmerPhone}</span>,
    },
    {
      key: 'units',
      label: 'Units Distributed',
      className: 'font-bold text-text-primary whitespace-nowrap',
      render: (row) => <span>{row.units} Units</span>,
    },
    {
      key: 'pricePerUnit',
      label: 'Price per Unit',
      className: 'text-text-secondary font-semibold whitespace-nowrap',
      render: (row) =>
        row.pricePerUnit === 0 ? (
          <span className="text-text-secondary/50">-</span>
        ) : (
          <span>₹{row.pricePerUnit.toLocaleString('en-IN')}</span>
        ),
    },
    {
      key: 'total',
      label: 'Total Value',
      className: 'font-bold text-text-primary whitespace-nowrap',
      render: (row) =>
        row.pricePerUnit === 0 ? (
          <span className="text-text-secondary/50">-</span>
        ) : (
          <span>₹{(row.units * row.pricePerUnit).toLocaleString('en-IN')}</span>
        ),
    },
  ]

  const formatReason = (reason?: string) => {
    if (!reason) return 'Quantity Mismatch'
    return reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const deficitUnits = grievance.affectedUnits || grievance.orderedUnits - grievance.receivedUnits

  return (
    <div className="space-y-6 text-left w-full overflow-x-hidden">
      {/* Page Header */}
      <PageHeader
        title={`Audit Ledger: ${grievance.id}`}
        description="Verify inventory dispatch from factory level down to farmer micro-distribution records."
        breadcrumbs={[
          { label: 'Portal', path: '/' },
          { label: 'Grievances', path: '/grievances' },
          { label: 'Audit Ledger' },
        ]}
        actions={
          <button
            onClick={() => navigate('/grievances')}
            className="px-3.5 py-2 text-xs font-bold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-all shadow-xs flex items-center gap-1.5 active:scale-95 shrink-0"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Queue
          </button>
        }
      />

      {/* Modern Executive KPI Metrics Strip */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* Card 1: Dispatched Quantity */}
        <div className="rounded-card border border-border bg-surface p-4 shadow-soft hover:shadow-card transition-all duration-300 flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-text-secondary block">
              Dispatched Quantity
            </span>
            <span className="text-lg sm:text-xl font-black text-text-primary tracking-tight block">
              {grievance.orderedUnits} Units
            </span>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary border border-primary shrink-0">
            <CatalogueIcon size={20} />
          </div>
        </div>

        {/* Card 2: Gate Received Quantity */}
        <div className="rounded-card border border-border bg-surface p-4 shadow-soft hover:shadow-card transition-all duration-300 flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-text-secondary block">
              Gate Received Quantity
            </span>
            <span className="text-lg sm:text-xl font-black text-status-active tracking-tight block">
              {grievance.receivedUnits} Units
            </span>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-status-active/10 text-status-active border border-status-active shrink-0">
            <DispatchIcon size={20} />
          </div>
        </div>

        {/* Card 3: Shortage Deficit */}
        <div className="rounded-card border border-border bg-surface p-4 shadow-soft hover:shadow-card transition-all duration-300 flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-danger block">
              Shortage Deficit
            </span>
            <span className="text-lg sm:text-xl font-black text-danger tracking-tight block">{deficitUnits} Units</span>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-danger/10 text-danger border border-danger shrink-0">
            <GrievancesIcon size={20} />
          </div>
        </div>

        {/* Card 4: Grievance Status */}
        <div className="rounded-card border border-border bg-surface p-4 shadow-soft hover:shadow-card transition-all duration-300 flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-text-secondary block">
              Audit Status
            </span>
            <div className="pt-0.5">
              <Badge
                tone={getGrievanceStatusTone(grievance.status)}
                variant="subtle"
                className="capitalize font-bold text-xs px-2.5 py-1"
              >
                {grievance.status.replace('_', ' ')}
              </Badge>
            </div>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-status-info/10 text-status-info border border-status-info shrink-0">
            <KYCVerificationIcon size={20} />
          </div>
        </div>
      </div>

      {/* Row 2: Buyer Banner Card & Grievance Status Audit Card Side-By-Side in One Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hero Card 1: Buyer Entity & Raised Grievance Summary (2 Columns) */}
        <div className="lg:col-span-2 rounded-card border border-border bg-surface p-4 sm:p-6 shadow-soft hover:shadow-card transition-all duration-300 space-y-5 flex flex-col justify-between">
          <div className="space-y-5">
            {/* Top Bar: Buyer Details & Category Badges */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/15 text-primary flex items-center justify-center font-black text-lg sm:text-xl shrink-0 border border-primary/30 shadow-xs">
                  {grievance.buyerName ? grievance.buyerName.charAt(0).toUpperCase() : 'B'}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base sm:text-lg font-black text-text-primary tracking-tight">
                      {grievance.buyerName || 'Unknown Buyer'}
                    </h2>
                    {grievance.buyerType && (
                      <Badge tone="secondary" variant="subtle" className="uppercase font-bold text-[10px] px-2 py-0.5">
                        {grievance.buyerType}
                      </Badge>
                    )}
                    <Badge tone="warning" variant="subtle" className="font-bold text-[10px] px-2 py-0.5">
                      {formatReason(grievance.reason)}
                    </Badge>
                  </div>
                  <span className="text-xs text-text-secondary font-medium block mt-0.5">
                    Grievance Audit & Verification File
                  </span>
                </div>
              </div>
            </div>

            {/* Middle Grid: Clean 3-Column Metadata Cards for IDs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-bg/60 border border-border p-3 rounded-card text-left space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-text-secondary block">
                  Raised By User ID
                </span>
                <span className="font-mono text-xs font-bold text-text-primary break-all block">
                  {grievance.raisedBy}
                </span>
              </div>
              <div className="bg-bg/60 border border-border p-3 rounded-card text-left space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-text-secondary block">
                  Full Grievance ID
                </span>
                <span className="font-mono text-xs font-bold text-text-primary break-all block">{grievance.id}</span>
              </div>
              <div className="bg-bg/60 border border-border p-3 rounded-card text-left space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-text-secondary block">
                  Linked Order ID
                </span>
                <span className="font-mono text-xs font-bold text-text-primary break-all block">
                  {grievance.orderId}
                </span>
              </div>
            </div>

            {/* Bottom Row: Evidence Attachments */}
            {grievance.evidenceUrls && grievance.evidenceUrls.length > 0 && (
              <div className="space-y-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider">
                      Evidence Attachments ({grievance.evidenceUrls.length}{' '}
                      {grievance.evidenceUrls.length === 1 ? 'Photo' : 'Photos'})
                    </h4>
                  </div>
                  <span className="text-[10px] font-semibold text-text-secondary">Click photo to enlarge</span>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  {grievance.evidenceUrls.map((url, idx) => (
                    <div
                      key={url}
                      onClick={() => setSelectedImage(url)}
                      className="relative group rounded-card overflow-hidden border border-border bg-bg/40 cursor-pointer h-20 w-20 sm:h-28 sm:w-28 shadow-sm hover:shadow-card transition-all duration-300 transform hover:-translate-y-0.5 shrink-0"
                    >
                      <img src={url} alt={`Evidence proof ${idx + 1}`} className="h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-white text-xs font-bold gap-1">
                        <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                          />
                        </svg>
                        <span>View</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hero Card 2: Grievance Status & Audit Card (1 Column in the same row) */}
        <div className="lg:col-span-1 border border-border p-4 sm:p-5 rounded-card bg-surface shadow-soft hover:shadow-card transition-all duration-300 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <h3 className="text-xs font-extrabold text-text-secondary uppercase tracking-wider border-b border-border pb-2.5">
              Grievance Audit
            </h3>
            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between items-center gap-2">
                <span className="text-text-secondary shrink-0">Assigned AM:</span>
                <span className="font-bold text-text-primary text-right">
                  {grievance.assignedAmName || 'Unassigned'}
                </span>
              </div>
              {grievance.assignedAmId && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-secondary text-[11px]">AM User ID:</span>
                  <span className="font-mono text-text-primary text-[11px] break-all">{grievance.assignedAmId}</span>
                </div>
              )}
              <div className="flex justify-between items-center gap-2 border-t border-border pt-2">
                <span className="text-text-secondary shrink-0">Logged Date:</span>
                <span className="font-semibold text-text-primary text-right">
                  {new Date(grievance.createdAt).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              {grievance.resolvedAt && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-text-secondary shrink-0">Resolved Date:</span>
                  <span className="font-semibold text-text-primary text-right">
                    {new Date(grievance.resolvedAt).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
              {grievance.resolvedBy && (
                <div className="flex flex-col gap-0.5 border-t border-border pt-2">
                  <span className="text-text-secondary text-[11px]">Resolved By User ID:</span>
                  <span className="font-mono text-text-primary text-[11px] break-all">{grievance.resolvedBy}</span>
                </div>
              )}
              {(grievance.resolutionNotes || grievance.notes) && (
                <div className="flex flex-col gap-1 border-t border-border pt-2 mt-1">
                  <span className="text-text-secondary font-bold uppercase text-[10px]">Resolution Notes:</span>
                  <p className="text-text-primary leading-normal italic font-medium break-words">
                    {grievance.resolutionNotes || grievance.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: FULL WIDTH Supply Chain Audit Timeline Ledger */}
      <div className="w-full space-y-6">
        {/* Main Section Container */}
        <div className="border border-border p-4 sm:p-6 md:p-7 rounded-card bg-surface shadow-soft hover:shadow-card transition-all duration-300 space-y-6 sm:space-y-8">
          {/* Header Bar */}
          <div className="flex items-center justify-between border-b border-border pb-4">
            <div className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse shrink-0" />
              <h3 className="text-xs font-extrabold text-text-primary uppercase tracking-wider">
                Supply Chain Audit Timeline Ledger
              </h3>
            </div>
          </div>

          {/* Stepper Grid Container */}
          <div className="relative space-y-8 sm:space-y-10 pl-0 sm:pl-2">
            {/* Vertical Connector Track */}
            <div className="absolute left-[20px] sm:left-[31px] top-5 sm:top-6 bottom-5 sm:bottom-6 w-0.5 bg-border" />

            {/* Step 1: Factory Dispatch */}
            <div className="relative flex items-start gap-3 sm:gap-4">
              {/* Step Circle Badge */}
              <div className="relative z-10 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-surface border-2 border-primary text-primary flex items-center justify-center font-black text-xs sm:text-sm shadow-md shrink-0">
                01
              </div>

              {/* Step Content Card */}
              <div className="flex-1 min-w-0 space-y-4 pt-0.5 sm:pt-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-bg/40 p-3 sm:p-3.5 rounded-card border border-border">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-extrabold text-primary uppercase tracking-wider">Stage 01</span>
                      <span className="text-text-secondary text-xs">•</span>
                      <h4 className="text-sm font-bold text-text-primary">
                        Factory Dispatch & Destination Gate Receipt
                      </h4>
                    </div>
                    <span className="text-xs text-text-secondary font-medium block mt-0.5">
                      Origin: Sanjeevani Setu Co. (Factory Warehouse)
                    </span>
                  </div>
                </div>

                {/* Dispatch summary stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-bg/60 p-3.5 sm:p-4 rounded-card border border-border text-left space-y-1">
                    <span className="text-[10px] text-text-secondary font-extrabold uppercase tracking-wider block">
                      Dispatched Quantity
                    </span>
                    <span className="text-lg sm:text-xl font-black text-text-primary block">
                      {grievance.orderedUnits} Units
                    </span>
                    <span className="text-[10px] text-text-secondary font-semibold block">
                      Standard Factory Invoice
                    </span>
                  </div>
                  <div className="bg-bg/60 p-3.5 sm:p-4 rounded-card border border-border text-left space-y-1">
                    <span className="text-[10px] text-text-secondary font-extrabold uppercase tracking-wider block">
                      Gate Received Quantity
                    </span>
                    <span className="text-lg sm:text-xl font-black text-status-active block">
                      {grievance.receivedUnits} Units
                    </span>
                    <span className="text-[10px] text-status-active font-semibold block">
                      Destination Receipt Count
                    </span>
                  </div>
                  <div className="bg-bg/60 p-3.5 sm:p-4 rounded-card border border-border text-left space-y-1">
                    <span className="text-[10px] text-danger font-extrabold uppercase tracking-wider block">
                      Shortage Deficit
                    </span>
                    <span className="text-lg sm:text-xl font-black text-danger block">{deficitUnits} Units</span>
                    <span className="text-[10px] text-danger font-semibold block">Claimed Deficit Shortage</span>
                  </div>
                </div>

                {/* Reported Discrepancy Quote Box */}
                <div className="bg-bg/60 p-3.5 sm:p-4 rounded-card border border-border text-xs space-y-1 text-left">
                  <div className="flex items-center gap-1.5 text-text-secondary font-bold uppercase text-[10px] tracking-wider">
                    <svg
                      className="h-3.5 w-3.5 text-primary shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                    <span>Reported Discrepancy Issue</span>
                  </div>
                  <p className="text-text-primary font-semibold leading-relaxed italic pt-0.5 break-words">
                    &ldquo;{grievance.issue}&rdquo;
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2: Farmer Distribution */}
            <div className="relative flex items-start gap-3 sm:gap-4">
              {/* Step Circle Badge */}
              <div className="relative z-10 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-surface border-2 border-status-active text-status-active flex items-center justify-center font-black text-xs sm:text-sm shadow-md shrink-0">
                02
              </div>

              {/* Step Content Card */}
              <div className="flex-1 min-w-0 space-y-4 pt-0.5 sm:pt-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-bg/40 p-3 sm:p-3.5 rounded-card border border-border">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-extrabold text-status-active uppercase tracking-wider">
                        Stage 02
                      </span>
                      <span className="text-text-secondary text-xs">•</span>
                      <h4 className="text-sm font-bold text-text-primary">FPO/VLCC Micro-Distribution to Farmers</h4>
                    </div>
                    <span className="text-xs text-text-secondary font-medium block mt-0.5">
                      Detailed ledger of units sold to individual local farmers ({farmersList.length} Farmers Recorded)
                    </span>
                  </div>
                </div>

                {/* Farmers Distribution Table */}
                <div className="space-y-3 min-w-0">
                  <div className="w-full overflow-x-auto">
                    <Table
                      columns={farmerColumns}
                      data={paginatedFarmers}
                      loading={false}
                      pagination={{
                        currentPage: page,
                        totalPages,
                        onPageChange: setPage,
                        totalItems,
                        itemsPerPage,
                      }}
                      emptyMessage="No records available."
                      emptyIcon={<GrievancesIcon size={20} className="opacity-60" />}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Card if order is available */}
        {order && (
          <div className="border border-border p-4 sm:p-5 rounded-card bg-surface shadow-soft hover:shadow-card transition-all duration-300 space-y-4">
            <h3 className="text-xs font-extrabold text-text-secondary uppercase tracking-wider border-b border-border pb-2.5 flex items-center justify-between">
              <span>Profile & Warehouse Info</span>
              <Badge tone="neutral" variant="outline" className="capitalize text-[10px] font-bold">
                {grievance.buyerType || order.buyer?.role || 'buyer'}
              </Badge>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div className="flex flex-col gap-0.5">
                <span className="text-text-secondary text-[11px]">Business / Entity Name:</span>
                <span className="font-bold text-text-primary text-sm break-words">
                  {grievance.buyerName || order.buyer?.businessName || order.buyer?.name || 'Unknown'}
                </span>
              </div>
              {order.buyer?.phone && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-secondary text-[11px]">Contact Phone:</span>
                  <span className="font-semibold text-text-primary font-mono">{order.buyer.phone}</span>
                </div>
              )}
              {order.buyer?.email && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-secondary text-[11px]">Email Address:</span>
                  <span className="font-medium text-text-primary break-all">{order.buyer.email}</span>
                </div>
              )}
            </div>
            {(order.shipToAddress || order.billToAddress) && (
              <div className="flex flex-col gap-0.5 border-t border-border pt-2.5 mt-2.5 text-xs">
                <span className="text-text-secondary text-[11px]">Warehouse Address:</span>
                <span className="font-semibold text-text-primary leading-normal break-words">
                  {[
                    (order.shipToAddress || order.billToAddress)?.street,
                    (order.shipToAddress || order.billToAddress)?.city,
                    (order.shipToAddress || order.billToAddress)?.state,
                    (order.shipToAddress || order.billToAddress)?.zip,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Image Preview Modal */}
      {selectedImage && (
        <Modal isOpen={!!selectedImage} onClose={() => setSelectedImage(null)} title="Evidence Images" size="lg">
          <div className="flex flex-col items-center justify-center p-2 space-y-4">
            <img
              src={selectedImage}
              alt="Evidence Full Preview"
              className="max-h-[70vh] w-auto rounded-card object-contain shadow-2xl border border-border"
            />
            <div className="flex justify-end w-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="px-5 py-2 text-xs font-bold bg-primary text-primary-fg rounded-btn shadow hover:bg-primary-hover transition-all active:scale-95"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
